import React from 'react';

import './tour-guide.css';
import { TourGuideProvider, type TourGuideProviderProps } from './TourGuideProvider';
import { ActionDock } from './ui/ActionDock';
import { DemoBookingButton } from './ui/DemoBookingButton';
import { PricingButton } from './ui/PricingButton';
import { TaskFlowTracker } from './ui/TaskFlowTracker';
import { TaskSpotlight } from './ui/TaskSpotlight';
import { TourLauncher } from './ui/TourLauncher';
import { TourTracker } from './ui/TourTracker';

export interface TourGuideProps extends TourGuideProviderProps {
  withLauncher?: boolean;
  withTracker?: boolean;
  withDemoBooking?: boolean;
  withPricing?: boolean;
  withTasks?: boolean;
}

export function TourGuide({
  withLauncher = true,
  withTracker = true,
  withDemoBooking = false,
  withPricing = false,
  withTasks = false,
  children,
  ...providerProps
}: TourGuideProps) {
  const anyButton = withLauncher || withDemoBooking || withPricing;

  return (
    <TourGuideProvider {...providerProps} tasks={withTasks}>
      {anyButton && (
        <ActionDock>
          {withLauncher && <TourLauncher />}
          {withPricing && <PricingButton />}
          {withDemoBooking && <DemoBookingButton />}
        </ActionDock>
      )}
      {withTracker && <TourTracker />}
      <TaskSpotlight />
      <TaskFlowTracker />
      {children}
    </TourGuideProvider>
  );
}

export { TourGuideProvider, useTourGuide, type TourGuideApi } from './TourGuideProvider';
export { TourLauncher } from './ui/TourLauncher';
export { TourTracker } from './ui/TourTracker';
export { HeaderSlot } from './ui/HeaderSlot';
export { ActionDock } from './ui/ActionDock';
export { DemoBookingButton } from './ui/DemoBookingButton';
export { DemoBookingDialog } from './ui/DemoBookingDialog';
export { PricingButton } from './ui/PricingButton';
export { TaskSpotlight } from './ui/TaskSpotlight';
export { TaskFlowTracker } from './ui/TaskFlowTracker';
export {
  TASKS,
  getTask,
  getTaskForStep,
  validateTasks,
  type TaskFlow,
  type TaskStep,
} from './tasks';
export {
  DEMO_OPTIONS,
  PLANS_URL,
  validateSandboxLinks,
  type DemoOption,
} from './sandboxLinks';
export {
  PRODUCT_TOUR,
  TOURS,
  WORKSPACES_TOUR_ID,
  buildWorkspacesTour,
  computeStageProgress,
  getTour,
  validateTours,
} from './tours';
export type {
  Tour,
  TourStep,
  TourStage,
  StageProgress,
  StepProgress,
  TourProgressView,
} from './tours';
export {
  createApiProgressStore,
  createLocalProgressStore,
  createMemoryProgressStore,
  type TourProgressStore,
} from './engine/progressStore';
export {
  HOST,
  resolveAnchor,
  sidebarAnchor,
  sidebarCardAnchor,
  submenuAnchor,
  type AnchorSpec,
} from './engine/anchors';

export default TourGuide;
